// Player-facing FT3D pause requests + ephemeral AFK presentation.
//
// The child scene owns the minimap control and overhead bubble. The campaign parent owns the
// authenticated request, Party-frame decoration, and campaign WebSocket. This bridge keeps those
// responsibilities separate and never treats AFK as a gameplay status effect.

import { ftCampaignClockIsRunning, ftSetCampaignClockRunning } from '../shared/campaign-clock-transport';

interface Ft3dPresenceEntry {
    playerCharacterId: string;
    entityId: string;
    displayName: string;
    state: 'afk' | 'ready';
    updatedAt: number;
    expiresAt: number;
}

interface Ft3dPresenceMessage {
    type?: 'ft3d_player_presence';
    schema?: 'ft.ft3d.player-presence.v1';
    campaign_id?: string;
    kind?: 'state' | 'pause_request';
    player_character_id?: string;
    entity_id?: string;
    display_name?: string;
    requested_at?: number;
    entry?: Ft3dPresenceEntry;
}

const presenceByPlayerId = new Map<string, Ft3dPresenceEntry>();
let presenceToastTimer: number | null = null;
let presenceExpiryTimer: number | null = null;
let partyObserver: MutationObserver | null = null;

function normalizedId(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
}

function presenceSurface(): HTMLElement | null {
    return document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
}

function presenceFrame(surface = presenceSurface()): HTMLIFrameElement | null {
    return surface?.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]') ?? null;
}

function activeEntries(): Ft3dPresenceEntry[] {
    const now = Date.now() / 1000;
    return [...presenceByPlayerId.values()].filter(entry => entry.state === 'afk' && entry.expiresAt > now);
}

function schedulePresenceExpiry(): void {
    if (presenceExpiryTimer !== null) {
        window.clearTimeout(presenceExpiryTimer);
        presenceExpiryTimer = null;
    }
    const nowMs = Date.now();
    const nextExpiryMs = [...presenceByPlayerId.values()]
        .filter(entry => entry.state === 'afk' && Number.isFinite(entry.expiresAt))
        .reduce<number | null>((nearest, entry) => {
            const candidate = entry.expiresAt * 1000;
            return nearest === null || candidate < nearest ? candidate : nearest;
        }, null);
    if (nextExpiryMs === null) return;
    presenceExpiryTimer = window.setTimeout(() => {
        presenceExpiryTimer = null;
        const expiredAt = Date.now() / 1000;
        let changed = false;
        for (const [playerId, entry] of presenceByPlayerId) {
            if (entry.state === 'afk' && entry.expiresAt <= expiredAt) {
                presenceByPlayerId.delete(playerId);
                changed = true;
            }
        }
        if (changed) {
            decoratePartyFrame();
            postPresenceToScene();
        }
        schedulePresenceExpiry();
    }, Math.max(0, Math.min(2_147_483_647, nextExpiryMs - nowMs + 25)));
}

function presenceEnvelope(): Record<string, unknown> {
    return {
        schema: 'ft.ft3d.player-presence.v1',
        entries: activeEntries(),
        queryOnly: true,
        writes: 0,
    };
}

function postPresenceToScene(): void {
    const frame = presenceFrame();
    if (!frame?.contentWindow) return;
    frame.contentWindow.postMessage({
        type: 'ft3d-campaign-player-presence-v1',
        payload: presenceEnvelope(),
    }, window.location.origin);
}

function decoratePartyFrame(): void {
    const active = new Map(activeEntries().map(entry => [normalizedId(entry.playerCharacterId), entry]));
    document.querySelectorAll<HTMLElement>('[data-ft3d-party-player-id]').forEach(member => {
        const entry = active.get(normalizedId(member.dataset.ft3dPartyPlayerId));
        member.dataset.ft3dPartyPresence = entry ? 'afk' : 'ready';
        member.classList.toggle('is-afk', Boolean(entry));
        const heading = member.querySelector<HTMLElement>('.ft3d-party-member__heading');
        let badge = member.querySelector<HTMLElement>('[data-ft3d-party-presence-badge]');
        if (!entry) {
            badge?.remove();
            return;
        }
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 'ft3d-party-member__presence-badge';
            badge.dataset.ft3dPartyPresenceBadge = '';
            badge.setAttribute('role', 'status');
            heading?.appendChild(badge);
        }
        // The roster observer watches childList changes. Avoid replacing an already-correct text
        // node on every pass, which would schedule the observer again indefinitely.
        if (badge.textContent !== 'AFK') badge.textContent = 'AFK';
        badge.setAttribute('aria-label', `${entry.displayName || 'Player'} is away from keyboard`);
    });
}

function applyPresenceEntry(entry: Ft3dPresenceEntry): void {
    const playerId = normalizedId(entry.playerCharacterId);
    const entityId = normalizedId(entry.entityId);
    if (!playerId || !entityId) return;
    const normalized: Ft3dPresenceEntry = {
        ...entry,
        playerCharacterId: playerId,
        entityId,
        displayName: String(entry.displayName || 'Player').trim().slice(0, 80),
        state: entry.state === 'afk' ? 'afk' : 'ready',
        updatedAt: Number(entry.updatedAt) || 0,
        expiresAt: Number(entry.expiresAt) || 0,
    };
    if (normalized.state === 'afk' && normalized.expiresAt > Date.now() / 1000) {
        presenceByPlayerId.set(playerId, normalized);
    } else {
        presenceByPlayerId.delete(playerId);
    }
    schedulePresenceExpiry();
    decoratePartyFrame();
    postPresenceToScene();
}

function replacePresence(entries: unknown): void {
    presenceByPlayerId.clear();
    if (Array.isArray(entries)) {
        entries.forEach(value => {
            if (!value || typeof value !== 'object' || Array.isArray(value)) return;
            const entry = value as Ft3dPresenceEntry;
            if (entry.state === 'afk') applyPresenceEntry(entry);
        });
    }
    schedulePresenceExpiry();
    decoratePartyFrame();
    postPresenceToScene();
}

function pauseFromPresenceToast(button: HTMLButtonElement): void {
    const surface = presenceSurface();
    if (!surface || surface.dataset.ft3dCanManage !== '1') return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    // Ask the clock, never the pause button's style.display. Reading the DOM here meant that in
    // in-combat chrome (both transport buttons display:none while the derived clock still runs) this
    // took the "already stopped" branch, broadcast a transport frame, changed nothing — and then told
    // the GM "Campaign time paused." A toast that lies about stopping the clock is worse than one
    // that admits it could not (2026-09-08).
    const wasRunning = ftCampaignClockIsRunning();
    // FORCED: this button is the GM's own explicit pause request, same as Hard Pause. Issue it even
    // when we believe time is already stopped — a stale local read must not swallow a pause of a
    // clock the server still has running (Codex review 2026-09-08).
    ftSetCampaignClockRunning(false, { force: true });
    // Players' transport pip is derived from the campaign blob, so this ack only matters for the
    // no-op case, where no clock write goes out to carry the news.
    if (!wasRunning) window.broadcastCampaignTransport?.('pause_request_accepted');
    if (ftCampaignClockIsRunning()) {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
        showPresenceToast('Campaign time could not be paused.', true, true);
        return;
    }
    showPresenceToast(wasRunning ? 'Campaign time paused.' : 'Campaign time was already stopped.', false);
}

function showPresenceToast(message: string, urgent = false, offerPause = false): void {
    const surface = presenceSurface();
    if (!surface) return;
    let toast = surface.querySelector<HTMLOutputElement>('[data-ft3d-player-presence-toast]');
    if (!toast) {
        toast = document.createElement('output');
        toast.className = 'ft3d-player-presence-toast';
        toast.dataset.ft3dPlayerPresenceToast = '';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'assertive');
        surface.appendChild(toast);
    }
    toast.classList.toggle('is-pause-request', urgent);
    toast.replaceChildren();
    const text = document.createElement('span');
    text.textContent = message;
    toast.appendChild(text);
    if (offerPause && surface.dataset.ft3dCanManage === '1'
        && surface.dataset.ft3dSessionMode !== 'paused') {
        const pauseButton = document.createElement('button');
        pauseButton.type = 'button';
        pauseButton.className = 'ft3d-player-presence-toast__pause';
        pauseButton.dataset.ft3dPauseRequestAccept = '';
        pauseButton.textContent = 'Pause now';
        pauseButton.addEventListener('click', () => pauseFromPresenceToast(pauseButton));
        toast.appendChild(pauseButton);
    }
    toast.hidden = false;
    if (presenceToastTimer !== null) window.clearTimeout(presenceToastTimer);
    presenceToastTimer = window.setTimeout(() => {
        if (toast) toast.hidden = true;
        presenceToastTimer = null;
    }, urgent ? 9000 : 5000);
}

function handlePresenceMessage(message: Ft3dPresenceMessage): void {
    if (message.schema !== 'ft.ft3d.player-presence.v1') return;
    const surfaceCampaignId = normalizedId(presenceSurface()?.dataset.campaignId);
    if (normalizedId(message.campaign_id) !== surfaceCampaignId) return;
    if (message.kind === 'state' && message.entry) {
        applyPresenceEntry(message.entry);
        return;
    }
    if (message.kind === 'pause_request') {
        const name = String(message.display_name || 'A player').trim().slice(0, 80);
        showPresenceToast(`${name} requested a pause.`, true, true);
    }
}

async function sendPresenceAction(action: 'toggle_afk' | 'request_pause', actorId: string): Promise<void> {
    const surface = presenceSurface();
    const campaignId = normalizedId(surface?.dataset.campaignId);
    if (!surface || !campaignId || !actorId) return;
    const frame = presenceFrame(surface);
    frame?.contentWindow?.postMessage({
        type: 'ft3d-campaign-player-presence-pending-v1',
        payload: { action, pending: true },
    }, window.location.origin);
    try {
        const response = await fetch(`${window.BASE_URL || '/'}options/api/ft3d_player_presence_inc.php`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ campaign_id: campaignId, actor_id: actorId, action }),
        });
        const result = await response.json().catch(() => null) as { ok?: boolean; error?: string; message?: Ft3dPresenceMessage } | null;
        if (!response.ok || result?.ok !== true || !result.message) {
            throw new Error(result?.error || `Presence request failed (HTTP ${response.status}).`);
        }
        handlePresenceMessage(result.message);
        if (action === 'request_pause') showPresenceToast('Pause request sent to the table.', false);
    } catch (error) {
        showPresenceToast(error instanceof Error ? error.message : 'Presence request failed.', true);
    } finally {
        frame?.contentWindow?.postMessage({
            type: 'ft3d-campaign-player-presence-pending-v1',
            payload: { action, pending: false },
        }, window.location.origin);
    }
}

async function fetchPresence(): Promise<void> {
    const surface = presenceSurface();
    const campaignId = normalizedId(surface?.dataset.campaignId);
    if (!surface || !campaignId) return;
    try {
        const response = await fetch(
            `${window.BASE_URL || '/'}options/api/ft3d_player_presence_inc.php?campaign_id=${encodeURIComponent(campaignId)}`,
            { credentials: 'same-origin', headers: { Accept: 'application/json' } },
        );
        const result = await response.json() as { ok?: boolean; schema?: string; entries?: unknown };
        if (response.ok && result.ok === true && result.schema === 'ft.ft3d.player-presence.v1') {
            replacePresence(result.entries);
        }
    } catch { /* A missing presence sidecar is a ready table, never a play-surface failure. */ }
}

export function initializeFt3dPlayerPresence(): void {
    const surface = presenceSurface();
    if (!surface || surface.dataset.ft3dPlayerPresenceMounted === '1') return;
    surface.dataset.ft3dPlayerPresenceMounted = '1';

    window.addEventListener('ft3d-player-presence-message', event => {
        handlePresenceMessage((event as CustomEvent<Ft3dPresenceMessage>).detail ?? {});
    });
    window.addEventListener('message', event => {
        const frame = presenceFrame(surface);
        if (event.origin !== window.location.origin || !frame || event.source !== frame.contentWindow) return;
        const envelope = event.data as { type?: string; payload?: Record<string, unknown> } | null;
        if (envelope?.type !== 'ft3d-campaign-player-presence-action-v1') return;
        const action = envelope.payload?.action;
        const actorId = normalizedId(envelope.payload?.actorId);
        if ((action === 'toggle_afk' || action === 'request_pause') && actorId) {
            void sendPresenceAction(action, actorId);
        }
    });

    const frame = presenceFrame(surface);
    frame?.addEventListener('load', postPresenceToScene);
    const roster = surface.querySelector('[data-ft3d-party-roster]');
    if (roster && typeof MutationObserver === 'function') {
        partyObserver = new MutationObserver(decoratePartyFrame);
        partyObserver.observe(roster, { childList: true, subtree: true });
    }
    void fetchPresence();
}

const mountPresence = (): void => initializeFt3dPlayerPresence();
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountPresence, { once: true });
else mountPresence();

declare global {
    interface Window {
        BASE_URL?: string;
        initializeFt3dPlayerPresence?: () => void;
        broadcastCampaignTransport?: (reason?: string) => void;
    }
}

window.initializeFt3dPlayerPresence = initializeFt3dPlayerPresence;
